import { useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { useInternalTelemetry } from '../useInternalTelemetry';
import { QosContext } from './QosContext';

/**
 * Component can use this React hook to report QoS status. The component must be a child of a QosProvider.
 * When isCritical is true, the component will become part of
 * the First Meaningful Paint (FMP).
 * @param componentName Name of the component
 * @param param1 {@link IQosHookOptions }
 * @returns callbacks to report QoS
 */
export var useQos = function useQos(componentName) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$disableWithoutCo = _ref.disableWithoutContext,
      disableWithoutContext = _ref$disableWithoutCo === void 0 ? false : _ref$disableWithoutCo,
      _ref$isCritical = _ref.isCritical,
      isCritical = _ref$isCritical === void 0 ? false : _ref$isCritical;

  var isReportPending = useRef(true);
  var qosContext = useContext(QosContext);

  var _useInternalTelemetry = useInternalTelemetry(),
      telemetry = _useInternalTelemetry.telemetry,
      registerToFirstMeaningfulContent = _useInternalTelemetry.registerToFirstMeaningfulContent,
      unRegisterToFirstMeaningfulContent = _useInternalTelemetry.unRegisterToFirstMeaningfulContent;

  var componentRef = useRef({
    componentName: componentName
  });
  var qos = useMemo(function () {
    if (qosContext) {
      return qosContext;
    }

    if (!disableWithoutContext) {
      telemetry.warn('QosProvider not found', {
        message: 'The component uses useQos hook must be wrapped by a QosProvider'
      });
    }

    return null;
  }, [qosContext, telemetry, disableWithoutContext]);
  useEffect(function () {
    componentRef.current.componentName = componentName;
  }, [componentName]); // When the isCritical changes, register or unregister accordingly

  useEffect(function () {
    var component = componentRef.current;

    if (isCritical) {
      registerToFirstMeaningfulContent(component);
    } else {
      unRegisterToFirstMeaningfulContent(component);
    }

    return function () {
      // Unregister when unmounts
      isCritical && unRegisterToFirstMeaningfulContent(component);
    };
  }, [isCritical, registerToFirstMeaningfulContent, unRegisterToFirstMeaningfulContent]);
  useEffect(function () {
    // Don't re-register with a new QosProvider if the component has already
    // reported a result (loadComplete or loadFailure), because the component may
    // not report again, which could cause the new QoS context to timeout.
    if (!isReportPending.current || qos === null) {
      return;
    }

    var component = componentRef.current;
    qos === null || qos === void 0 ? void 0 : qos.registerComponent(component);
    return function () {
      qos === null || qos === void 0 ? void 0 : qos.unregisterComponent(component);
    };
  }, [qos, registerToFirstMeaningfulContent, unRegisterToFirstMeaningfulContent]);
  return {
    reportLoadComplete: useCallback(function () {
      isReportPending.current = false;
      qos === null || qos === void 0 ? void 0 : qos.reportLoadComplete(componentRef.current); // Every time a critical component reports complete, check if the first meaningful paint is complete

      isCritical && unRegisterToFirstMeaningfulContent(componentRef.current);
    }, [qos, isCritical, unRegisterToFirstMeaningfulContent]),
    reportFailure: useCallback(function (error) {
      isReportPending.current = false;
      qos === null || qos === void 0 ? void 0 : qos.reportFailure(componentRef.current, error);
    }, [qos])
  };
};
//# sourceMappingURL=useQos.js.map